import { toast } from 'sonner';
import { useRef } from 'react';
import { markReviewQueueDirty } from '@/utils/reviewQueueEvents';
import { staleRunPlan } from '@/utils/offlineRunPlan';

export function useReviewActions(owner, reload) {
  const busyOwner = useRef(null);
  return async (item, options) => {
    const attempt = {};
    const result = await owner.execute(item, { ...options,
      start: () => { busyOwner.current = attempt; options.start?.(); },
      finish: () => {
        if (busyOwner.current === attempt) { busyOwner.current = null; options.finish?.(); }
      },
    });
    const warning = result.accepted ? options.warning?.(result.cloudValue) : null;
    if (result.accepted) {
      markReviewQueueDirty(!warning && result.state === 'accepted' ? options.reason : 'review_queue_changed',
        result.state === 'accepted' ? owner.opening : undefined);
    }
    if (result.state === 'retired' || result.state === 'blocked') return false;
    if (result.state === 'accepted' || result.state === 'read') {
      options.accept?.(result.value, result.cloudValue);
      if (result.state === 'accepted') await reload();
      if (owner.current() && options.message) {
        if (warning) toast.warning(warning);
        else toast.success(typeof options.message === 'function' ? options.message(result.cloudValue) : options.message);
      }
      return owner.current();
    }
    if (result.state === 'local-pending') toast.error('Cloud accepted this action, but local recording is unconfirmed. Retry only the local acknowledgement below.');
    else if (result.state === 'unknown') toast.error('The action outcome is unconfirmed. This item remains held; no automatic repeat was sent.');
    else if (staleRunPlan(result.error)) {
      try { await reload(); } catch { /* Preserve the known refusal if refresh is unavailable. */ }
      if (owner.current()) toast.error('This offline plan changed. Review the refreshed plan before trying your change again.');
    } else if (!options.reject?.(result.error)) {
      const detail = result.error?.response?.data?.detail;
      toast.error((typeof detail === 'string' ? detail : detail?.message) || options.failure || 'Review action could not complete');
    }
    return false;
  };
}
